import { useEffect, useState } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { postePunktesystemAufBoard } from './boardMeldung';
import BonusDeadlines from './BonusDeadlines';

// Bonusfragen-Konfiguration: welche Fragen aktiv sind und was sie wert
// sind (saisonKonfiguration.bonusfragen, Feldnamen unverändert) plus die
// Deadline-Überschreibung.

const STANDARD = {
  preSeasonPredictions: {
    divisionWinners: { aktiv: true, punkte: 15 },
    conferenceSeed1: { aktiv: true, punkte: 20 },
    wildCardTeams: { aktiv: true, punkte: 10 },
    superBowlWinner: { aktiv: true, punkte: 50 },
    mvp: { aktiv: true, punkte: 30 }
  },
  playoffPredictions: {
    conferenceChampionshipTeams: { aktiv: true, punkte: 15 },
    superBowlTeams: { aktiv: true, punkte: 20 },
    superBowlWinner: { aktiv: true, punkte: 25 }
  }
};

const FRAGEN = {
  preSeasonPredictions: [
    ['divisionWinners', '🏆 Division Winners', '8 Teams · Punkte je Treffer'],
    ['conferenceSeed1', '1️⃣ Conference #1 Seeds', '2 Teams · Punkte je Treffer'],
    ['wildCardTeams', '🃏 Wild Card Teams', '6 Teams · Punkte je Treffer'],
    ['superBowlWinner', '🏆 Super Bowl Champion', '1 Team'],
    ['mvp', '⭐ MVP', '1 Spieler']
  ],
  playoffPredictions: [
    ['conferenceChampionshipTeams', '🏈 Conference Championship Teams', '4 Teams · Punkte je Treffer'],
    ['superBowlTeams', '🏆 Super Bowl Teams', '2 Teams · Punkte je Treffer'],
    ['superBowlWinner', '👑 Super Bowl Sieger', '1 Team']
  ]
};

function mitStandard(cfg) {
  const out = { preSeasonPredictions: {}, playoffPredictions: {} };
  ['preSeasonPredictions', 'playoffPredictions'].forEach(g => {
    Object.keys(STANDARD[g]).forEach(k => {
      out[g][k] = { ...STANDARD[g][k], ...(cfg?.[g]?.[k] || {}) };
    });
  });
  return out;
}

function BonusKonfiguration({ saisonId, saisonName, config, neuLaden, zeigeToast }) {
  const { userProfile } = useAuth();
  const [bf, setBf] = useState(STANDARD);
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => { setBf(mitStandard(config?.bonusfragen)); }, [config]);

  const geaendert = JSON.stringify(bf) !== JSON.stringify(mitStandard(config?.bonusfragen));
  const set = (gruppe, frage, feld, wert) =>
    setBf(b => ({ ...b, [gruppe]: { ...b[gruppe], [frage]: { ...b[gruppe][frage], [feld]: wert } } }));

  const speichern = async () => {
    setLaeuft(true);
    try {
      await setDoc(doc(db, 'saisonKonfiguration', saisonId), {
        saison: saisonName,
        bonusfragen: bf,
        letzteAenderung: new Date().toISOString(),
        geaendertVon: userProfile?.displayName || 'Admin'
      }, { merge: true });
      if (config?.punkteSystem) {
        try {
          await postePunktesystemAufBoard(userProfile, saisonName, config.punkteSystem, bf);
        } catch (e) {
          console.error('Board-Meldung fehlgeschlagen:', e);
        }
      }
      await neuLaden();
      zeigeToast('Bonusfragen-Konfiguration gespeichert');
    } catch (error) {
      zeigeToast('Fehler: ' + error.message, true);
    } finally {
      setLaeuft(false);
    }
  };

  // Als Funktion (nicht als Komponente), damit die Eingabefelder beim Tippen nicht neu gemountet werden
  const block = (gruppe, titel, hinweis) => (
    <div className="karte" key={gruppe}>
      <h2>{titel}</h2>
      <p className="karte__hinweis">{hinweis}</p>
      {FRAGEN[gruppe].map(([k, label, sub]) => {
        const f = bf[gruppe][k];
        return (
          <div key={k} className={`zeile${f.aktiv ? '' : ' zeile--aus'}`}>
            <div className="zeile__text">
              <div className="zeile__label">{label}</div>
              <div className="zeile__hinweis">{sub}</div>
            </div>
            <div className="zeile__eingabe">
              <input type="number" className="zahl-input" value={f.punkte ?? ''} min={0} step={0.5} disabled={!f.aktiv}
                onChange={e => set(gruppe, k, 'punkte', e.target.value === '' ? '' : parseFloat(e.target.value))} />
              <button type="button" className={`schalter-button${f.aktiv ? ' ist-an' : ''}`} onClick={() => set(gruppe, k, 'aktiv', !f.aktiv)}>
                {f.aktiv ? 'AN' : 'AUS'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      {block('preSeasonPredictions', '📅 Pre-Season Predictions', 'Deadline: erstes Spiel der Saison (oder Überschreibung unten).')}
      {block('playoffPredictions', '🏆 Playoff Predictions', 'Deadline: erstes Playoff-Spiel (oder Überschreibung unten).')}

      <div className="karte karte--gelb">
        <button type="button" className="aktion" onClick={speichern} disabled={laeuft || !geaendert}>
          {laeuft ? '⏳ Speichere…' : '💾 Bonusfragen-Konfiguration speichern'}
        </button>
      </div>

      <BonusDeadlines saisonId={saisonId} config={config} />
    </>
  );
}

export default BonusKonfiguration;
